import React, { ComponentProps } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { formatWeight } from 'utils/weight'
import { ProductCounterRoot, Counter } from './ProductCounter.styles'
import { hasWeightLimit, ProductDetail, ShoppingCartItem } from 'models/Product'

type ProductCounterOwnProps = ComponentProps<typeof ProductCounterRoot>
export type ProductCounterProps = Omit<ProductCounterOwnProps, 'onChange'> & {
  product: ShoppingCartItem | ProductDetail
  quantity?: number
  onChange: (productId: string, quantity: number) => void
  small?: boolean
}

export const ProductCounter = ({
  product,
  quantity = 0,
  onChange,
  small = false,
  ...rest
}: ProductCounterProps) => {
  const { t } = useTranslation()
  const { productId, name, weight } = product

  const decrease = () => {
    onChange(productId, quantity - 1)
  }

  const increase = () => {
    onChange(productId, quantity + 1)
  }

  return (
    <ProductCounterRoot {...rest}>
      <Stack
        direction={small ? 'column' : 'row'}
        gap={small ? '3' : '5'}
        alignItems={small ? 'flex-start' : 'center'}
      >
        <Stack alignItems="center">
          <Button
            size="small"
            aria-label={t('e-commerce:detail.remove', { productName: name })}
            variant="tertiary"
            icon="minus"
            onClick={decrease}
            disabled={!quantity}
          />
          <Counter justifyContent="center" alignItems="center">
            <input
              min="0"
              type="number"
              value={quantity}
              onChange={(e) => onChange(productId, Math.abs(Number(e.target.value)))}
              aria-label={t('e-commerce:detail.enter-quantity', { productName: name })}
            />
          </Counter>
          <Button
            size="small"
            aria-label={t('e-commerce:detail.add', { productName: name })}
            variant="tertiary"
            icon="plus"
            onClick={increase}
          />
        </Stack>
        {hasWeightLimit(product) && (
          <Text color="tertiary-600">
            <Text weight="medium">{t('e-commerce:detail.total-weight')}&nbsp;</Text>
            <Text weight="medium">{formatWeight(quantity * weight)}</Text>
          </Text>
        )}
        {product.unitWeight && (
          <Text weight="medium" color="tertiary-600">
            {`${product.weight / product.unitWeight} x ${product.unitWeight}oz`}
          </Text>
        )}
      </Stack>
    </ProductCounterRoot>
  )
}

ProductCounter.displayName = 'ProductCounter'
